import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
} from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[twTimeInput]',
  standalone: true,
})
export class TimeInputDirective {
  @Input() format: 'HH:mm' | 'HH:mm:ss' = 'HH:mm:ss';

  constructor(
    private ngControl: NgControl,
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
  ) {}

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  @HostListener('blur')
  onBlur(): void {
    const raw = this.ngControl.control?.value?.toString().trim() ?? '';

    if (!raw) {
      this.ngControl.control?.setValue('', { emitEvent: false });
      return;
    }

    // Remove non-digit except colon for splitting
    const cleaned = raw.replace(/[^0-9:]/g, '');

    let hh = 0,
      mm = 0,
      ss = 0;

    if (cleaned.includes(':')) {
      const parts = cleaned.split(':');
      hh = parseInt(parts[0], 10) || 0;
      mm = parseInt(parts[1], 10) || 0;
      ss = this.format === 'HH:mm:ss' ? parseInt(parts[2], 10) || 0 : 0;
    } else {
      // No colon: interpret first 2 digits as hours, next 2 as minutes, etc.
      const digits = cleaned.replace(/\D/g, '');
      hh = parseInt(digits.substring(0, 2), 10) || 0;
      mm = parseInt(digits.substring(2, 4), 10) || 0;
      ss =
        this.format === 'HH:mm:ss'
          ? parseInt(digits.substring(4, 6), 10) || 0
          : 0;
    }

    // Clamp values
    hh = this.clamp(hh, 0, 23); // hours between 0 and 23
    mm = this.clamp(mm, 0, 59);
    ss = this.clamp(ss, 0, 59);

    // Format padded strings
    const hhStr = hh.toString().padStart(2, '0');
    const mmStr = mm.toString().padStart(2, '0');
    const ssStr = ss.toString().padStart(2, '0');

    let formatted = `${hhStr}:${mmStr}`;
    if (this.format === 'HH:mm:ss') {
      formatted += `:${ssStr}`;
    }

    this.ngControl.control?.setValue(formatted, { emitEvent: false });
    this.renderer.setProperty(this.el.nativeElement, 'value', formatted);
  }
  @HostListener('input', ['$event'])
  onInput(event: any) {
    const input = event.target as HTMLInputElement;
    // Remove all non-digits and limit length
    const digits = input.value
      .replace(/\D/g, '')
      .slice(0, this.format === 'HH:mm:ss' ? 6 : 4);
    // Split into pairs of 2
    const parts = digits.match(/.{1,2}/g) || [];
    // Join with colon
    const formatted = parts.join(':');

    // Update input and form control value
    input.value = formatted;
    this.ngControl.control?.setValue(formatted, { emitEvent: false });
    this.renderer.setProperty(this.el.nativeElement, 'value', formatted);
  }
}
